import { motion, useReducedMotion } from "framer-motion";
import {
    LayoutDashboard,
    Package,
    ShoppingBag,
    Store as StoreIcon,
    CheckCircle2,
    Truck,
} from "lucide-react";

/**
 * Mockup estático del panel del vendedor. Es una ilustración de nuestro propio
 * producto — no muestra tiendas ni productos reales de los merchants.
 */
const NAV = [
    [LayoutDashboard, "Resumen", true],
    [StoreIcon, "Mi tienda", false],
    [Package, "Productos", false],
    [ShoppingBag, "Pedidos", false],
];

const STATS = [
    ["Ventas del mes", "$18,450"],
    ["Pedidos", "37"],
    ["Productos", "24"],
];

const ORDERS = [
    ["TND-4K2M9P", "Pagado", "$1,290", "bg-emerald-50 text-emerald-700"],
    ["TND-8QW3ZA", "Guía generada", "$640", "bg-primary-subtle text-primary-hover"],
    ["TND-2HN7BC", "Enviado", "$2,150", "bg-amber-50 text-amber-700"],
];

export default function DashboardMockup() {
    const reduce = useReducedMotion();

    return (
        <div
            className="relative mx-auto max-w-5xl"
            role="img"
            aria-label="Vista previa del panel de Tiendify: resumen de ventas, pedidos y productos de una tienda."
        >
            <div className="rounded-2xl sm:rounded-3xl border border-zinc-200 bg-white shadow-2xl shadow-black/25 overflow-hidden">
                {/* Barra superior */}
                <div className="flex items-center gap-2 px-4 h-11 border-b border-zinc-200 bg-[#F6F6F3]">
                    <span className="w-2.5 h-2.5 rounded-full bg-zinc-300" />
                    <span className="w-2.5 h-2.5 rounded-full bg-zinc-300" />
                    <span className="w-2.5 h-2.5 rounded-full bg-zinc-300" />
                    <div className="mx-auto px-4 h-6 rounded-md bg-white border border-zinc-200 grid place-items-center">
                        <span className="text-[11px] text-zinc-500">
                            tiendify.io/dashboard
                        </span>
                    </div>
                </div>

                <div className="flex">
                    {/* Sidebar */}
                    <div className="hidden sm:block w-44 shrink-0 border-r border-zinc-200 p-3">
                        {NAV.map(([Icon, label, active]) => (
                            <div
                                key={label}
                                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium mb-0.5 ${
                                    active
                                        ? "bg-primary-subtle text-primary-hover"
                                        : "text-zinc-500"
                                }`}
                            >
                                <Icon className="w-4 h-4" aria-hidden="true" />
                                {label}
                            </div>
                        ))}
                    </div>

                    {/* Contenido */}
                    <div className="flex-1 min-w-0 p-4 sm:p-6">
                        <div className="grid grid-cols-3 gap-3 sm:gap-4">
                            {STATS.map(([label, value]) => (
                                <div
                                    key={label}
                                    className="rounded-xl border border-zinc-200 p-3 sm:p-4"
                                >
                                    <p className="text-[11px] sm:text-xs text-zinc-500 truncate">
                                        {label}
                                    </p>
                                    <p className="mt-1 text-base sm:text-2xl font-bold tracking-tight text-[#1A1A1A] tabular-nums">
                                        {value}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div className="mt-4 sm:mt-6 rounded-xl border border-zinc-200 overflow-hidden">
                            <div className="px-3 sm:px-4 py-2.5 border-b border-zinc-200 bg-[#F6F6F3]">
                                <p className="text-[11px] sm:text-xs font-semibold text-zinc-600">
                                    Pedidos recientes
                                </p>
                            </div>
                            {ORDERS.map(([number, status, total, tone]) => (
                                <div
                                    key={number}
                                    className="flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 border-b border-zinc-100 last:border-b-0"
                                >
                                    <span className="text-[11px] sm:text-xs font-medium text-zinc-700 tabular-nums">
                                        {number}
                                    </span>
                                    <span
                                        className={`px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] font-medium ${tone}`}
                                    >
                                        {status}
                                    </span>
                                    <span className="ml-auto text-[11px] sm:text-xs font-semibold text-[#1A1A1A] tabular-nums">
                                        {total}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Tarjeta flotante — pago */}
            <motion.div
                initial={reduce ? false : { opacity: 0, x: -16, y: 8 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: reduce ? 0 : 0.5, delay: 0.5, ease: "easeOut" }}
                className="hidden sm:flex absolute -left-4 lg:-left-10 bottom-10 items-center gap-3 rounded-2xl border border-zinc-200 bg-white shadow-lg p-3.5 max-w-[230px]"
            >
                <div className="w-9 h-9 rounded-full bg-emerald-50 grid place-items-center shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                    <p className="text-xs font-semibold text-[#1A1A1A]">
                        Pago recibido
                    </p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                        Tarjeta · $1,290 MXN
                    </p>
                </div>
            </motion.div>

            {/* Tarjeta flotante — envío */}
            <motion.div
                initial={reduce ? false : { opacity: 0, x: 16, y: -8 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: reduce ? 0 : 0.5, delay: 0.65, ease: "easeOut" }}
                className="hidden sm:flex absolute -right-4 lg:-right-10 top-16 items-center gap-3 rounded-2xl border border-zinc-200 bg-white shadow-lg p-3.5 max-w-[240px]"
            >
                <div className="w-9 h-9 rounded-full bg-primary-subtle grid place-items-center shrink-0">
                    <Truck className="w-4 h-4 text-primary" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                    <p className="text-xs font-semibold text-[#1A1A1A]">
                        Guía lista
                    </p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                        Descarga el PDF y envía
                    </p>
                </div>
            </motion.div>
        </div>
    );
}
